import { defineStore } from 'pinia';
import { ref } from 'vue';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export const useNotificationStore = defineStore('notification', () => {
  const isVisible = ref(false);
  const message = ref('');
  const title = ref('');
  const type = ref<NotificationType>('info');

  function show(options: {
    message: string;
    title?: string;
    type?: NotificationType;
  }) {
    message.value = options.message;
    title.value = options.title || '';
    type.value = options.type || 'info';
    isVisible.value = true;
  }

  function hide() {
    isVisible.value = false;
  }

  // Convenience methods
  function success(msg: string, titleText?: string) {
    show({ message: msg, title: titleText, type: 'success' });
  }

  function error(msg: string, titleText?: string) {
    show({ message: msg, title: titleText || 'Error', type: 'error' });
  }

  function warning(msg: string, titleText?: string) {
    show({ message: msg, title: titleText, type: 'warning' });
  }

  function info(msg: string, titleText?: string) {
    show({ message: msg, title: titleText, type: 'info' });
  }

  return {
    isVisible,
    message,
    title,
    type,
    show,
    hide,
    success,
    error,
    warning,
    info
  };
});

// Composable for easy usage
export function useNotification() {
  const store = useNotificationStore();
  return {
    show: store.show,
    success: store.success,
    error: store.error,
    warning: store.warning,
    info: store.info,
    hide: store.hide
  };
}
